import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  toBase, fromBase, altsFor, fmt, UNITS,
  SpecCard, SpecValue, SuffixNum,
} from "./_shared";

/**
 *  CHEMICAL DOSING
 *  --------------------------------------------------------------
 *  Mass needed:   m (mg/h) = C_target (mg/L) × Q (L/h)
 *  Stock at C_s (mg/mL):  Volume (mL/h) = m / C_s
 *
 *  Stock concentration inputs:
 *    % w/v  → 1 % = 10 mg/mL
 *    g/L    → 1 g/L = 1 mg/mL
 *    mg/L (= ppm)  → divide by 1000 for mg/mL
 *  --------------------------------------------------------------
 */
const STOCK_UNITS = [
  { value: "pct_wv", label: "% w/v (g per 100 mL)" },
  { value: "gL",     label: "g/L" },
  { value: "mgL",    label: "mg/L (ppm)" },
];

function stockMgPerMl(stockVal, stockUnit) {
  switch (stockUnit) {
    case "pct_wv": return stockVal * 10;       // 1 % w/v = 10 mg/mL
    case "gL":     return stockVal;            // 1 g/L = 1 mg/mL
    case "mgL":    return stockVal / 1000;     // 1 mg/L = 0.001 mg/mL
    default:       return 0;
  }
}

export default function DosingCalculator() {
  const [chemical, setChemical] = useState("nahypo12");    // preset
  const [targetVal, setTargetVal] = useState(1);
  const [targetUnit, setTargetUnit] = useState("mgL");

  const [flowVal, setFlowVal] = useState(5);
  const [flowUnit, setFlowUnit] = useState("m3h");

  const [stockVal, setStockVal] = useState(12);
  const [stockUnit, setStockUnit] = useState("pct_wv");

  const [hoursPerDay, setHoursPerDay] = useState(24);

  // When the editor picks "Custom — enter stock concentration below"
  // they're working with a chemical we haven't preset (a specific brand,
  // a polyamine, a proprietary antiscalant blend). Free-text product name
  // flows through to the dosing-spec, on-screen result, PDF export and
  // hand-off note instead of the generic word "stock".
  const [customChemicalName, setCustomChemicalName] = useState("");

  // Mixing-tank panel — reverse-engineers tank recipe from dosing-pump rate
  const [dosingPumpLh, setDosingPumpLh] = useState(4);    // L/h read off the pump curve at pumpBar
  const [mixingTankL, setMixingTankL] = useState(100);    // L tank we're mixing into

  // ─── Analog pump operating point ──────────────────────────────────
  // Two readings the operator takes straight off the manufacturer's
  // pump curve: a system pressure (bar) and the L/h the curve shows
  // AT that pressure (with the stroke at 100 %). On the analog dial
  // the operator then sets a stroke % (0-100 %) to scale the delivery.
  //
  //   pumpBar       — system pressure the pump runs at on site.
  //   dosingPumpLh  — L/h the pump-curve indicates at pumpBar @ 100 %
  //                   stroke (NOT the pump's absolute max).
  //   pumpStrokePct — analog stroke dial. Active rate = pumpLh × stroke%
  const [pumpBar, setPumpBar] = useState(1);
  const [pumpStrokePct, setPumpStrokePct] = useState(100);
  const activeDosingLh = dosingPumpLh * (pumpStrokePct / 100);

  // ─── Bidirectional mode ────────────────────────────────────────────
  // "recipe" = forward (given target ppm → solve mixing recipe).
  // "ppm"    = reverse (given recipe + pump settings → solve resulting
  //            process ppm). The two modes share every input above this
  //            block; only the dose / recipe inputs swap.
  const [mode, setMode] = useState("recipe");
  // Reverse-mode inputs: what the operator actually poured into the tank.
  const [chemicalAddedL, setChemicalAddedL] = useState(5);
  const [waterAddedL, setWaterAddedL] = useState(95);

  // Apply preset stock concentration when user picks a common chemical.
  const PRESETS = [
    { id: "nahypo12", label: "Sodium hypochlorite (NaOCl) — 12 % liquid", stockVal: 12, stockUnit: "pct_wv" },
    { id: "nahypo15", label: "Sodium hypochlorite (NaOCl) — 15 % liquid", stockVal: 15, stockUnit: "pct_wv" },
    { id: "cahypo65", label: "Calcium hypochlorite (Ca(OCl)₂) — 65 % granular", stockVal: 65, stockUnit: "pct_wv" },
    { id: "alum",     label: "Aluminium sulphate — 8 % w/v",  stockVal: 8,  stockUnit: "pct_wv" },
    { id: "fecl3",    label: "Ferric chloride — 40 % w/v",     stockVal: 40, stockUnit: "pct_wv" },
    { id: "naoh20",   label: "Caustic soda (NaOH) — 20 % w/v", stockVal: 20, stockUnit: "pct_wv" },
    { id: "polyalum", label: "PAC (polyaluminium chloride) — 18 % w/v", stockVal: 18, stockUnit: "pct_wv" },
    { id: "antiscalant", label: "RO antiscalant — neat dose (target mg/L direct)", stockVal: 100, stockUnit: "pct_wv" },
    { id: "custom",   label: "Custom — enter stock concentration below", stockVal: null, stockUnit: null },
  ];

  React.useEffect(() => {
    const p = PRESETS.find((x) => x.id === chemical);
    if (p && p.stockVal != null) {
      setStockVal(p.stockVal);
      setStockUnit(p.stockUnit);
    }
  }, [chemical]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convert target dose to mg/L (mg/L = ppm in dilute water)
  const target_mgL = targetUnit === "mgL" ? targetVal
                  : targetUnit === "ppm" ? targetVal
                  : targetUnit === "gL"  ? targetVal * 1000
                  : targetVal;

  // Resolve the human-readable chemical label used by the export, the
  // dosing specification line, and the on-screen narrative.
  //   • "custom" preset  → the editor's typed product name (or a
  //                        generic "custom chemical" if left blank).
  //   • any other preset → the preset label followed by the editor's
  //                        typed brand / SKU in brackets if provided.
  //                        e.g. "Antiscalant / scale inhibitor (Vitec 3000)".
  // The trimmed product-name doubles as a "brand tag" so the PDF
  // spec sentence and the on-screen SpecCard always name the exact
  // product the operator will actually pour, not just the category.
  const trimmedProductName = (customChemicalName || "").trim();
  const chemicalLabel = chemical === "custom"
    ? (trimmedProductName || "custom chemical")
    : trimmedProductName
      ? `${PRESETS.find((p) => p.id === chemical)?.label ?? chemical} (${trimmedProductName})`
      : (PRESETS.find((p) => p.id === chemical)?.label ?? chemical);

  // Convert flow to L/h
  const flow_m3s = toBase("flow", flowVal, flowUnit);
  const flow_Lh  = flow_m3s * 3600 * 1000;

  const stock_mgPerMl = stockMgPerMl(stockVal, stockUnit);
  // Volume of stock per hour = (target × flow) / stock_mgPerMl
  const dose_mlPerHr = stock_mgPerMl > 0 ? (target_mgL * flow_Lh) / stock_mgPerMl : null;
  const dose_mlPerMin = dose_mlPerHr !== null ? dose_mlPerHr / 60 : null;
  const daily_L = dose_mlPerHr !== null ? (dose_mlPerHr * hoursPerDay) / 1000 : null;
  const monthly_L = daily_L !== null ? daily_L * 30 : null;
  // Mass of active ingredient consumed per day, in kg
  const daily_active_kg = (target_mgL * flow_Lh * hoursPerDay) / 1e6;

  // Sanity status: very high dose rate hints at undersized pump
  const doseStatus = dose_mlPerHr === null ? undefined
                  : dose_mlPerHr > 10000  ? "warn"
                  : dose_mlPerHr < 1       ? "warn"
                  : "ok";
  const doseNote = dose_mlPerHr === null
    ? null
    : dose_mlPerHr > 10000
    ? "Over 10 L/h — exceeds the range of most diaphragm dosing pumps; specify a larger metering pump or stronger stock."
    : dose_mlPerHr < 1
    ? "Below 1 mL/h — too low for accurate diaphragm dosing; dilute the stock or use a peristaltic pump."
    : "Within typical diaphragm metering-pump range.";

  // ───────── MIXING TANK — reverse engineering ─────────
  //   required_active_mgh = target_mgL × flow_Lh           (mg/h of active)
  //   diluted_conc_mgL    = required_active_mgh / active_dosing_Lh
  //   tank_active_mg      = diluted_conc_mgL × mixing_tank_L
  //   chemical_vol_L      = tank_active_mg / stock_mgPerMl / 1000
  //   water_vol_L         = mixing_tank_L − chemical_vol_L
  //   tank_runtime_h      = mixing_tank_L / active_dosing_Lh
  // ``active_dosing_Lh`` is the pump-curve max × stroke %, NOT the raw
  // pump capacity — analog pumps at 50 % stroke only deliver half their
  // curve max, and the dilution maths has to use what'll actually flow.
  const requiredActive_mgh = target_mgL * flow_Lh;
  const dilutedConc_mgL = activeDosingLh > 0 ? requiredActive_mgh / activeDosingLh : null;
  const chemical_vol_L = dilutedConc_mgL !== null && stock_mgPerMl > 0
    ? (dilutedConc_mgL * mixingTankL) / stock_mgPerMl / 1000
    : null;
  const water_vol_L = chemical_vol_L !== null ? mixingTankL - chemical_vol_L : null;
  const chemical_pct = chemical_vol_L !== null && mixingTankL > 0
    ? (chemical_vol_L / mixingTankL) * 100
    : null;
  const tank_runtime_h = activeDosingLh > 0 ? mixingTankL / activeDosingLh : null;
  const tank_runtime_days = tank_runtime_h !== null && hoursPerDay > 0
    ? tank_runtime_h / hoursPerDay : null;

  // Status: can the dosing pump even deliver enough at any feasible mix?
  const mixStatus = chemical_vol_L === null ? undefined
                  : chemical_vol_L > mixingTankL ? "alert"
                  : chemical_pct > 60            ? "warn"
                  : chemical_pct < 0.1            ? "warn"
                  : "ok";
  const mixNote = chemical_vol_L === null
    ? null
    : chemical_vol_L > mixingTankL
    ? `Required chemical volume (${fmt(chemical_vol_L, 1)} L) exceeds the tank. Increase the dosing pump rate, the stock strength, or the tank size.`
    : chemical_pct > 60
    ? `Solution is ${fmt(chemical_pct, 0)} % chemical — very concentrated. Consider a larger tank or higher dosing-pump rate.`
    : chemical_pct < 0.1
    ? `Solution is ${fmt(chemical_pct, 3)} % chemical — extremely dilute. A smaller tank or lower-rate pump gives better accuracy.`
    : `Solution is ${fmt(chemical_pct, 1)} % chemical · tank lasts ${fmt(tank_runtime_h, 0)} h continuous (${fmt(tank_runtime_days, 1)} days at ${hoursPerDay} h/day).`;

  // ───────── REVERSE — given recipe + pump, compute resulting process ppm ─────────
  //   total_mixed_L      = water_added_L + chemical_added_L
  //   diluted_conc_mgL   = (chemical_added_L × 1000 × stock_mgPerMl) ÷ total_mixed_L
  //   active_mgh         = diluted_conc_mgL × active_dosing_Lh
  //   resulting_ppm_mgL  = active_mgh ÷ flow_Lh
  //   tank_runtime_h     = total_mixed_L ÷ active_dosing_Lh
  // The actual mixed volume is (water + chemical) — the mixing tank size
  // is used purely as a validation cap (overflow warning) so the operator
  // is told when their pour will spill the tank.
  const totalMixed_L = (Number(waterAddedL) || 0) + (Number(chemicalAddedL) || 0);
  const rev_dilutedConc_mgL = totalMixed_L > 0 && stock_mgPerMl > 0
    ? ((Number(chemicalAddedL) || 0) * 1000 * stock_mgPerMl) / totalMixed_L
    : null;
  const rev_chemical_pct = totalMixed_L > 0
    ? ((Number(chemicalAddedL) || 0) / totalMixed_L) * 100
    : null;
  const rev_active_mgh = rev_dilutedConc_mgL !== null
    ? rev_dilutedConc_mgL * activeDosingLh
    : null;
  // Resulting ppm in the process water — this is the headline output.
  const rev_resulting_mgL = rev_active_mgh !== null && flow_Lh > 0
    ? rev_active_mgh / flow_Lh
    : null;
  const rev_runtime_h = activeDosingLh > 0 ? totalMixed_L / activeDosingLh : null;
  const rev_runtime_days = rev_runtime_h !== null && hoursPerDay > 0
    ? rev_runtime_h / hoursPerDay : null;
  const rev_daily_L = rev_runtime_h && rev_runtime_h > 0
    ? (Number(chemicalAddedL) || 0) * (hoursPerDay / rev_runtime_h)
    : null;
  const rev_monthly_L = rev_daily_L !== null ? rev_daily_L * 30 : null;
  const rev_daily_active_kg = rev_resulting_mgL !== null && flow_Lh > 0
    ? (rev_resulting_mgL * flow_Lh * hoursPerDay) / 1e6
    : null;
  const tankFillPct = mixingTankL > 0 ? (totalMixed_L / mixingTankL) * 100 : 0;
  const rev_tankStatus = totalMixed_L > mixingTankL ? "alert"
    : totalMixed_L < mixingTankL * 0.5 ? "warn"
    : "ok";
  const rev_tankNote = totalMixed_L > mixingTankL
    ? `Recipe (${fmt(totalMixed_L, 1)} L) overflows the ${fmt(mixingTankL, 0)} L tank by ${fmt(totalMixed_L - mixingTankL, 1)} L. Reduce one of the volumes or upsize the tank.`
    : totalMixed_L < mixingTankL * 0.5
    ? `Recipe only fills ${fmt(tankFillPct, 0)} % of the ${fmt(mixingTankL, 0)} L tank. Tank is under-filled — mix larger batches or downsize the tank.`
    : `Recipe fills ${fmt(totalMixed_L, 1)} L of the ${fmt(mixingTankL, 0)} L tank (${fmt(tankFillPct, 0)} %).`;
  // Sanity status on the resulting ppm — flag obvious operator mistakes
  // (no chemical poured, no water poured making it concentrated stock,
  // pump stroke at 0 %, no flow set).
  const rev_ppmStatus = rev_resulting_mgL === null ? undefined
    : (Number(chemicalAddedL) || 0) <= 0 ? "warn"
    : (Number(waterAddedL) || 0) <= 0 ? "alert"
    : activeDosingLh <= 0 ? "alert"
    : "ok";
  const rev_ppmNote = rev_resulting_mgL === null
    ? "Need a non-zero stock concentration, recipe and pump setting to compute the resulting dose."
    : (Number(chemicalAddedL) || 0) <= 0
    ? "Zero chemical added — resulting dose is 0 mg/L."
    : (Number(waterAddedL) || 0) <= 0
    ? "Zero water added — the tank holds neat stock; pump will deliver undiluted chemical at the full stock concentration."
    : activeDosingLh <= 0
    ? "Pump stroke is 0 % — pump delivers no flow, no chemical reaches the process."
    : `${fmt(rev_resulting_mgL, 2)} mg/L of active chemical reaches the process water.`;

  return (
    <ToolShell
      slug="dosing"
      title="Chemical dosing"
      oneLiner="Stock dose rate for any soluble chemical — chlorine, coagulant, caustic, antiscalant. Pick a preset or enter the stock strength. Switch direction: solve a mixing recipe from a target dose, OR solve the resulting process dose from a recipe you already poured."
      onExport={() => mode === "recipe" ? ({
        inputs: [
          { label: "Calculation mode", value: "Recipe from target dose" },
          { label: "Chemical", value: chemicalLabel },
          { label: "Target dose", value: targetVal, unit: targetUnit === "mgL" ? "mg/L" : targetUnit },
          { label: "Process flow", value: flowVal, unit: UNITS.flow.labels[flowUnit] },
          { label: "Stock concentration", value: stockVal, unit: STOCK_UNITS.find((u) => u.value === stockUnit)?.label ?? stockUnit },
          { label: "Operating hours per day", value: hoursPerDay, unit: "h/day" },
          { label: "System pressure", value: pumpBar, unit: "bar",
            note: "System pressure the pump runs at — used to read the L/h reading from the pump curve." },
          { label: "Pump curve reading", value: dosingPumpLh, unit: `L/h @ ${pumpBar} bar`,
            note: "Flow shown on the manufacturer's pump curve at this pressure (at 100 % stroke)." },
          { label: "Pump stroke setting", value: pumpStrokePct, unit: "%",
            note: pumpStrokePct < 100
              ? `Operator dials the analog pump to ${pumpStrokePct} % — delivers ${fmt(activeDosingLh, 2)} L/h of the ${dosingPumpLh} L/h pump-curve reading.`
              : "Stroke at 100 % — pump runs at the curve reading above." },
        ],
        results: [
          { label: "Dose rate", value: fmt(dose_mlPerHr), unit: "mL/h",
            alts: dose_mlPerHr !== null ? [{ value: fmt(dose_mlPerMin), unit: "mL/min" }] : null,
            status: doseStatus, note: doseNote },
          { label: "Daily stock consumption", value: fmt(daily_L, 2), unit: "L / day",
            alts: daily_L !== null && daily_L >= 1 ? [{ value: fmt(daily_L * 1000, 0), unit: "mL" }] : null },
          { label: "Monthly stock consumption", value: fmt(monthly_L, 1), unit: "L / month" },
          { label: "Active ingredient consumed", value: fmt(daily_active_kg, 3), unit: "kg / day",
            note: "Mass of active chemical actually delivered to the process." },
          { label: "Dosing pump curve reading", value: dosingPumpLh, unit: `L/h @ ${pumpBar} bar` },
          { label: "Pump stroke setting", value: pumpStrokePct, unit: "% of curve reading" },
          { label: "Active dosing rate", value: fmt(activeDosingLh, 2), unit: "L/h",
            note: pumpStrokePct < 100
              ? `${dosingPumpLh} L/h × ${pumpStrokePct} % stroke = ${fmt(activeDosingLh, 2)} L/h delivered to the dilution tank.`
              : "Stroke at 100 % — active rate equals the pump-curve reading." },
          { label: "Mixing tank size", value: mixingTankL, unit: "L" },
          { label: "Mixed solution concentration", value: fmt(dilutedConc_mgL, 0), unit: "mg/L",
            note: `${fmt(chemical_pct, 2)} % w/v in the mixing tank.` },
          { label: "Chemical to add", value: fmt(chemical_vol_L, 2), unit: "L",
            status: mixStatus, note: mixNote },
          { label: "Water to add", value: fmt(water_vol_L, 2), unit: "L" },
          { label: "Tank runtime", value: fmt(tank_runtime_h, 0), unit: "h continuous",
            note: tank_runtime_days !== null ? `${fmt(tank_runtime_days, 1)} days at ${hoursPerDay} h/day operation.` : null },
        ],
        notes: [
          "Stock dose (mL/h) = ( target_mg/L × flow_L/h ) ÷ stock concentration (mg/mL).",
          "1 % w/v = 10 mg/mL · 1 g/L = 1 mg/mL · 1 mg/L = 0.001 mg/mL.",
          "Mixing tank recipe: chemical mass needed = (target_mg/L × flow_L/h) ÷ dosing_pump_L/h × tank_L.  Chemical volume = mass ÷ stock_mg/mL.",
        ],
        spec: dose_mlPerHr !== null && chemical_vol_L !== null && chemical_vol_L <= mixingTankL ? {
          title: "Dosing specification",
          body: `Dose <strong>${fmt(dose_mlPerHr, 1)} mL/h</strong> of ${chemicalLabel} to hit <strong>${targetVal} ${targetUnit === "mgL" ? "mg/L" : targetUnit}</strong> in <strong>${flowVal} ${UNITS.flow.labels[flowUnit]}</strong>. At <strong>${pumpBar} bar</strong> the pump curve reads <strong>${dosingPumpLh} L/h</strong> at 100 % stroke; ${pumpStrokePct < 100 ? `set the analog dial to <strong>${pumpStrokePct} %</strong> to deliver <strong>${fmt(activeDosingLh, 2)} L/h</strong>.` : `run the pump at 100 % stroke to deliver the full <strong>${dosingPumpLh} L/h</strong>.`} Mixing recipe for the <strong>${mixingTankL} L</strong> tank: <strong>${fmt(chemical_vol_L, 2)} L</strong> of stock + <strong>${fmt(water_vol_L, 2)} L</strong> water, lasts <strong>${fmt(tank_runtime_h, 0)} h</strong> continuous (${fmt(tank_runtime_days, 1)} days at ${hoursPerDay} h/day). Daily consumption ≈ <strong>${fmt(daily_L, 2)} L</strong> of stock and <strong>${fmt(daily_active_kg, 3)} kg</strong> of active ingredient.`,
        } : null,
      }) : ({
        inputs: [
          { label: "Calculation mode", value: "Resulting dose from recipe" },
          { label: "Chemical", value: chemicalLabel },
          { label: "Process flow", value: flowVal, unit: UNITS.flow.labels[flowUnit] },
          { label: "Stock concentration", value: stockVal, unit: STOCK_UNITS.find((u) => u.value === stockUnit)?.label ?? stockUnit },
          { label: "Operating hours per day", value: hoursPerDay, unit: "h/day" },
          { label: "System pressure", value: pumpBar, unit: "bar" },
          { label: "Pump curve reading", value: dosingPumpLh, unit: `L/h @ ${pumpBar} bar` },
          { label: "Pump stroke setting", value: pumpStrokePct, unit: "%",
            note: pumpStrokePct < 100
              ? `Operator dial at ${pumpStrokePct} % — active dose flow ${fmt(activeDosingLh, 2)} L/h.`
              : "Stroke at 100 % — pump runs at the full curve reading." },
          { label: "Mixing tank size", value: mixingTankL, unit: "L" },
          { label: "Chemical added to tank", value: chemicalAddedL, unit: "L" },
          { label: "Water added to tank", value: waterAddedL, unit: "L" },
          { label: "Total mixed volume", value: fmt(totalMixed_L, 2), unit: "L",
            note: rev_tankNote, status: rev_tankStatus },
        ],
        results: [
          { label: "Resulting process dose", value: fmt(rev_resulting_mgL, 2), unit: "mg/L",
            alts: rev_resulting_mgL !== null ? [{ value: fmt(rev_resulting_mgL, 2), unit: "ppm" }] : null,
            status: rev_ppmStatus, note: rev_ppmNote },
          { label: "Mixed solution concentration", value: fmt(rev_dilutedConc_mgL, 0), unit: "mg/L",
            note: rev_chemical_pct !== null ? `${fmt(rev_chemical_pct, 2)} % w/v in the mixing tank.` : null },
          { label: "Active mass delivered", value: fmt(rev_active_mgh, 0), unit: "mg/h",
            alts: rev_active_mgh !== null ? [{ value: fmt(rev_active_mgh / 1000, 2), unit: "g/h" }] : null,
            note: "Mass of active chemical the pump pushes into the process per hour." },
          { label: "Tank runtime", value: fmt(rev_runtime_h, 1), unit: "h continuous",
            note: rev_runtime_days !== null ? `${fmt(rev_runtime_days, 1)} days at ${hoursPerDay} h/day operation.` : null },
          { label: "Daily stock consumption", value: fmt(rev_daily_L, 2), unit: "L / day" },
          { label: "Monthly stock consumption", value: fmt(rev_monthly_L, 1), unit: "L / month" },
          { label: "Active ingredient consumed", value: fmt(rev_daily_active_kg, 3), unit: "kg / day" },
        ],
        notes: [
          "Mixed concentration (mg/L) = ( chemical_L × 1000 × stock_mg/mL ) ÷ ( chemical_L + water_L ).",
          "Active mass to process (mg/h) = mixed_mg/L × active_pump_L/h.",
          "Resulting process dose (mg/L) = active_mg/h ÷ process_flow_L/h.",
          "1 % w/v = 10 mg/mL · 1 g/L = 1 mg/mL · 1 mg/L = 0.001 mg/mL.",
        ],
        spec: rev_resulting_mgL !== null && (Number(chemicalAddedL) || 0) > 0 ? {
          title: "Resulting-dose specification",
          body: `Pouring <strong>${fmt(chemicalAddedL, 2)} L</strong> of ${chemicalLabel} into <strong>${fmt(waterAddedL, 2)} L</strong> of water makes a <strong>${fmt(rev_dilutedConc_mgL, 0)} mg/L</strong> (<strong>${fmt(rev_chemical_pct, 2)} % w/v</strong>) solution in the <strong>${mixingTankL} L</strong> tank. At <strong>${pumpBar} bar</strong> the pump curve reads <strong>${dosingPumpLh} L/h</strong>; ${pumpStrokePct < 100 ? `at <strong>${pumpStrokePct} %</strong> stroke the pump delivers <strong>${fmt(activeDosingLh, 2)} L/h</strong>` : `at 100 % stroke the pump delivers the full <strong>${dosingPumpLh} L/h</strong>`} into <strong>${flowVal} ${UNITS.flow.labels[flowUnit]}</strong> of process water — that's a resulting dose of <strong>${fmt(rev_resulting_mgL, 2)} mg/L</strong>. Tank lasts <strong>${fmt(rev_runtime_h, 1)} h</strong> continuous (${fmt(rev_runtime_days, 1)} days at ${hoursPerDay} h/day). Daily stock consumption ≈ <strong>${fmt(rev_daily_L, 2)} L</strong>, active ingredient ≈ <strong>${fmt(rev_daily_active_kg, 3)} kg</strong>.`,
        } : null,
      })}
    >
      {/* ───────── Calculation mode toggle ─────────
          Bidirectional: recipe (forward) vs ppm (reverse). Other inputs
          (flow, chemical preset, pump settings, hours/day) stay populated
          across mode switches — only the dose / recipe inputs swap. */}
      <div
        className="mb-5 p-1 inline-flex items-stretch"
        data-testid="dose-mode-toggle"
        style={{
          background: "#F4F2EC",
          border: "1px solid rgba(12,42,64,0.10)",
          borderRadius: 999,
          gap: 2,
        }}
      >
        {[
          { v: "recipe", label: "Recipe from target dose", hint: "Forward · solve mixing recipe" },
          { v: "ppm",    label: "Resulting dose from recipe", hint: "Reverse · solve process ppm" },
        ].map((opt) => {
          const active = mode === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              onClick={() => setMode(opt.v)}
              data-testid={`dose-mode-${opt.v}`}
              title={opt.hint}
              style={{
                padding: "8px 16px",
                background: active ? "#0C2A40" : "transparent",
                color: active ? "#FFFFFF" : "rgba(12,42,64,0.7)",
                fontSize: "0.78rem",
                fontWeight: active ? 700 : 500,
                letterSpacing: "0.02em",
                border: "none",
                borderRadius: 999,
                cursor: "pointer",
                transition: "all 0.18s ease",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Chemical / preset" span={2}>
          <Select value={chemical} onChange={setChemical} options={PRESETS.map((p) => ({ value: p.id, label: p.label }))} testid="dose-preset" />
        </Field>
        {/* Product-name / brand SKU field — visible for EVERY preset,
            not just "custom", so operators can name the specific product
            they're actually using (e.g. Antiscalant → Vitec 3000, PAC →
            Ferripol, NaOCl → Nalclean 90%). Empty leaves the spec sheet
            reading with just the generic preset label. */}
        <Field
          label={chemical === "custom" ? "Product name" : "Product name / brand SKU"}
          span={2}
          hint={chemical === "custom"
            ? "Used on-screen, in the dosing specification, and in the exported PDF."
            : "Optional — adds the exact product/brand alongside the preset label on the exported PDF and the on-screen specification."}
        >
          <input
            type="text"
            value={customChemicalName}
            onChange={(e) => setCustomChemicalName(e.target.value)}
            data-testid="dose-custom-name"
            placeholder={chemical === "custom"
              ? 'e.g. "Ferripol-S coagulant 30 % w/v" or your brand SKU'
              : `e.g. "${PRESETS.find((p) => p.id === chemical)?.label?.split(" ")[0] || "Brand"} SKU / product code"`}
            className="w-full px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
            style={{
              borderColor: "rgba(12,42,64,0.16)",
              color: "#0C2A40",
              fontSize: "0.95rem",
              fontWeight: 500,
            }}
          />
        </Field>
        {mode === "recipe" ? (
          <Field label="Target dose" hint="The mg/L (ppm) you want the process water to receive. The calculator solves the mixing recipe.">
            <div className="flex items-stretch">
              <div className="flex-1 min-w-0">
                <SuffixNum value={targetVal} onChange={setTargetVal} testid="dose-target" />
              </div>
              <select value={targetUnit} onChange={(e) => setTargetUnit(e.target.value)}
                className="px-2 border border-l-0 bg-[#FAFAF7] outline-none focus:border-[#1FB6C1]"
                style={{ borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.78)", fontSize: "0.75rem", minWidth: 78 }}
                data-testid="dose-target-unit">
                <option value="mgL">mg/L</option>
                <option value="ppm">ppm</option>
                <option value="gL">g/L</option>
              </select>
            </div>
          </Field>
        ) : (
          <Field label="Chemical added to tank" hint="Volume of stock chemical you poured into the mixing tank.">
            <SuffixNum value={chemicalAddedL} onChange={setChemicalAddedL} suffix="L" testid="dose-chem-added" />
          </Field>
        )}
        <Field label="Process flow">
          <UnitNumber dim="flow" value={flowVal} onChange={setFlowVal} unit={flowUnit} onUnitChange={setFlowUnit}
            units={["Lps", "Lpm", "m3h", "m3d", "gpm"]} testid="dose-flow" />
        </Field>
        <Field label="Stock concentration" hint="Editable — pre-loaded from the preset.">
          <div className="flex items-stretch">
            <div className="flex-1 min-w-0">
              <SuffixNum value={stockVal} onChange={setStockVal} testid="dose-stock" />
            </div>
            <select value={stockUnit} onChange={(e) => setStockUnit(e.target.value)}
              className="px-2 border border-l-0 bg-[#FAFAF7] outline-none focus:border-[#1FB6C1]"
              style={{ borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.78)", fontSize: "0.72rem", minWidth: 130 }}
              data-testid="dose-stock-unit">
              {STOCK_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
        </Field>
        {mode === "ppm" && (
          <Field label="Water added to tank" hint="Volume of water you poured into the mixing tank alongside the chemical.">
            <SuffixNum value={waterAddedL} onChange={setWaterAddedL} suffix="L" testid="dose-water-added" />
          </Field>
        )}
        <Field label="Operating hours per day" span={mode === "ppm" ? 1 : 2}>
          <SuffixNum value={hoursPerDay} onChange={setHoursPerDay} suffix="h/day" testid="dose-hours" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {mode === "recipe" ? (
          <>
            <Result label="Dose rate"
              primary={{ value: fmt(dose_mlPerHr), unit: "mL/h" }}
              alts={dose_mlPerHr !== null ? [{ value: fmt(dose_mlPerMin), unit: "mL/min" }] : null}
              status={doseStatus} note={doseNote}
              testid="dose-result-rate" />
            <Result label="Daily stock"
              primary={{ value: fmt(daily_L, 2), unit: "L / day" }}
              alts={daily_L !== null && daily_L < 1 ? [{ value: fmt(daily_L * 1000, 0), unit: "mL" }] : null}
              testid="dose-result-daily" />
            <Result label="Monthly stock"
              primary={{ value: fmt(monthly_L, 1), unit: "L / month" }}
              testid="dose-result-monthly" />
            <Result label="Active ingredient"
              primary={{ value: fmt(daily_active_kg, 3), unit: "kg / day" }}
              note="Mass of active chemical delivered."
              testid="dose-result-active" />
          </>
        ) : (
          <>
            <Result label="Resulting process dose"
              primary={{ value: fmt(rev_resulting_mgL, 2), unit: "mg/L" }}
              alts={rev_resulting_mgL !== null ? [{ value: fmt(rev_resulting_mgL, 2), unit: "ppm" }] : null}
              status={rev_ppmStatus} note={rev_ppmNote}
              testid="dose-result-resulting-ppm" />
            <Result label="Daily stock"
              primary={{ value: fmt(rev_daily_L, 2), unit: "L / day" }}
              alts={rev_daily_L !== null && rev_daily_L < 1 ? [{ value: fmt(rev_daily_L * 1000, 0), unit: "mL" }] : null}
              testid="dose-result-rev-daily" />
            <Result label="Monthly stock"
              primary={{ value: fmt(rev_monthly_L, 1), unit: "L / month" }}
              testid="dose-result-rev-monthly" />
            <Result label="Active ingredient"
              primary={{ value: fmt(rev_daily_active_kg, 3), unit: "kg / day" }}
              note="Mass of active chemical delivered to the process."
              testid="dose-result-rev-active" />
          </>
        )}
      </div>

      {/* ───────── Mixing-tank panel ─────────
          Mode-aware: in "recipe" mode it reverse-engineers the tank
          recipe from a target ppm; in "ppm" mode it confirms the tank
          recipe the operator already poured and shows what concentration
          actually reaches the process. */}
      <div
        className="mt-5 p-5 md:p-6"
        style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}
      >
        <div
          className="font-mono uppercase mb-4"
          style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}
        >
          {mode === "recipe"
            ? "Mixing tank · reverse from dosing pump"
            : "Mixing tank · resulting concentration"}
        </div>

        <div className="grid md:grid-cols-2 gap-5 mb-5">
          <Field
            label="Pump curve reading — flow at pressure"
            hint="L/h the manufacturer's pump curve shows at the system pressure below (at 100 % stroke). Just a curve reading — NOT the pump's absolute max."
          >
            <SuffixNum value={dosingPumpLh} onChange={setDosingPumpLh} suffix="L/h" testid="dose-pump" />
          </Field>
          <Field
            label="Mixing tank size"
            hint="Volume of the dilution tank you're mixing into."
          >
            <SuffixNum value={mixingTankL} onChange={setMixingTankL} suffix="L" testid="dose-tank" />
          </Field>
        </div>

        {/* ─── Analog pump operating point ─────────────────────────────
            Two readings the operator takes straight off the pump curve:
            the system pressure (bar) and the L/h the curve shows at that
            pressure (at 100 % stroke). The analog stroke dial then scales
            the actual delivery. Active dose = pumpLh × stroke %. */}
        <div className="grid md:grid-cols-2 gap-5 mb-5">
          <Field
            label="System pressure (from pump curve)"
            hint="System pressure the pump will run at. Read the pump curve at THIS pressure to get the L/h above."
          >
            <SuffixNum value={pumpBar} onChange={setPumpBar} suffix="bar" testid="dose-pump-bar" />
          </Field>
          <Field
            label="Pump stroke setting"
            hint="Analog dial — what the operator sets on site. 100 % = pump runs at the curve reading above."
          >
            <SuffixNum value={pumpStrokePct} onChange={setPumpStrokePct} suffix="%" testid="dose-pump-stroke" />
          </Field>
        </div>

        {/* Active dosing rate read-out — the live multiplication that
            ties the curve reading + stroke % together. Shown prominently
            so the operator can sanity-check before walking to the pump. */}
        <div
          className="mb-5 p-3"
          data-testid="dose-active-rate-card"
          style={{
            background: "rgba(31,182,193,0.07)",
            border: "1px solid rgba(31,182,193,0.30)",
            borderRadius: 8,
          }}
        >
          <div
            className="font-mono uppercase mb-1"
            style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                     color: "#0E7A82", fontWeight: 700 }}
          >
            Active dosing rate
          </div>
          <div style={{ fontSize: "0.92rem", color: "#0C2A40", lineHeight: 1.5 }}>
            At <strong>{pumpBar} bar</strong> the pump curve reads{" "}
            <strong>{dosingPumpLh} L/h</strong> (at 100 % stroke). Set the
            analog dial to <strong>{pumpStrokePct} %</strong> — the pump
            delivers <strong style={{ color: "#0E7A82" }}>
            {fmt(activeDosingLh, 2)} L/h</strong> of diluted solution into
            the process{mode === "recipe"
              ? " — this is the rate the mixing-tank recipe below is sized for."
              : " — combined with the mixed solution concentration this determines the resulting ppm reaching the process."}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {mode === "recipe" ? (
            <>
              <Result label="Mixed solution conc."
                primary={{ value: fmt(dilutedConc_mgL, 0), unit: "mg/L" }}
                alts={dilutedConc_mgL !== null ? [
                  { value: fmt(chemical_pct, 2), unit: "% w/v" },
                ] : null}
                note="What the mixing tank should contain after dilution."
                testid="dose-result-mix-conc" />
              <Result label="Chemical to add"
                primary={{ value: fmt(chemical_vol_L, 2), unit: "L" }}
                alts={chemical_vol_L !== null && chemical_vol_L < 1
                  ? [{ value: fmt(chemical_vol_L * 1000, 0), unit: "mL" }] : null}
                status={mixStatus}
                note={mixNote}
                testid="dose-result-chem-vol" />
              <Result label="Water to add"
                primary={{ value: fmt(water_vol_L, 2), unit: "L" }}
                note={chemical_pct !== null ? `Top up to ${fmt(mixingTankL, 0)} L total.` : null}
                testid="dose-result-water-vol" />
              <Result label="Tank runtime"
                primary={{ value: fmt(tank_runtime_h, 0), unit: "h" }}
                alts={tank_runtime_days !== null ? [{ value: fmt(tank_runtime_days, 1), unit: `days @ ${hoursPerDay} h/d` }] : null}
                note="Continuous run-time before the tank empties."
                testid="dose-result-runtime" />
            </>
          ) : (
            <>
              <Result label="Mixed solution conc."
                primary={{ value: fmt(rev_dilutedConc_mgL, 0), unit: "mg/L" }}
                alts={rev_dilutedConc_mgL !== null ? [
                  { value: fmt(rev_chemical_pct, 2), unit: "% w/v" },
                ] : null}
                note={`What ${fmt(chemicalAddedL, 2)} L of stock in ${fmt(totalMixed_L, 1)} L total mixed volume produces.`}
                testid="dose-result-rev-mix-conc" />
              <Result label="Tank fill"
                primary={{ value: fmt(totalMixed_L, 2), unit: "L" }}
                alts={[{ value: fmt(tankFillPct, 0), unit: `% of ${fmt(mixingTankL, 0)} L` }]}
                status={rev_tankStatus}
                note={rev_tankNote}
                testid="dose-result-rev-tank-fill" />
              <Result label="Active mass delivered"
                primary={{ value: fmt(rev_active_mgh, 0), unit: "mg/h" }}
                alts={rev_active_mgh !== null && rev_active_mgh >= 1000
                  ? [{ value: fmt(rev_active_mgh / 1000, 2), unit: "g/h" }] : null}
                note="Mass of active chemical the pump pushes into the process per hour."
                testid="dose-result-rev-active-mgh" />
              <Result label="Tank runtime"
                primary={{ value: fmt(rev_runtime_h, 1), unit: "h" }}
                alts={rev_runtime_days !== null ? [{ value: fmt(rev_runtime_days, 1), unit: `days @ ${hoursPerDay} h/d` }] : null}
                note="Continuous run-time before this batch is consumed."
                testid="dose-result-rev-runtime" />
            </>
          )}
        </div>
      </div>

      {mode === "recipe" && dose_mlPerHr !== null && chemical_vol_L !== null && chemical_vol_L <= mixingTankL && (
        <SpecCard
          eyebrow="Dosing specification"
          testid="dose-spec-card"
          footer={mixNote}
        >
          Dose <SpecValue>{fmt(dose_mlPerHr, 1)} mL/h</SpecValue> of{" "}
          {chemicalLabel} to hit{" "}
          <SpecValue>{targetVal} {targetUnit === "mgL" ? "mg/L" : targetUnit}</SpecValue> in{" "}
          <SpecValue>{flowVal} {UNITS.flow.labels[flowUnit]}</SpecValue>. At{" "}
          <SpecValue>{pumpBar} bar</SpecValue> the pump curve reads{" "}
          <SpecValue>{dosingPumpLh} L/h</SpecValue> at 100 % stroke;{" "}
          {pumpStrokePct < 100 ? (
            <>set the analog dial to{" "}
              <SpecValue>{pumpStrokePct} %</SpecValue> to deliver{" "}
              <SpecValue>{fmt(activeDosingLh, 2)} L/h</SpecValue>.{" "}
            </>
          ) : (
            <>run the pump at 100 % stroke to deliver the full{" "}
              <SpecValue>{dosingPumpLh} L/h</SpecValue>.{" "}
            </>
          )}
          Mixing recipe for the{" "}
          <SpecValue>{mixingTankL} L</SpecValue> tank:{" "}
          <SpecValue>{fmt(chemical_vol_L, 2)} L</SpecValue> of stock +{" "}
          <SpecValue>{fmt(water_vol_L, 2)} L</SpecValue> water, lasts{" "}
          <SpecValue>{fmt(tank_runtime_h, 0)} h</SpecValue> continuous ({fmt(tank_runtime_days, 1)} days at {hoursPerDay} h/day). Daily consumption ≈{" "}
          <SpecValue>{fmt(daily_L, 2)} L</SpecValue> of stock and{" "}
          <SpecValue>{fmt(daily_active_kg, 3)} kg</SpecValue> of active ingredient.
        </SpecCard>
      )}

      {mode === "ppm" && rev_resulting_mgL !== null && (Number(chemicalAddedL) || 0) > 0 && (
        <SpecCard
          eyebrow="Resulting-dose specification"
          testid="dose-spec-card-rev"
          footer={rev_tankNote}
        >
          Pouring <SpecValue>{fmt(chemicalAddedL, 2)} L</SpecValue> of{" "}
          {chemicalLabel} into{" "}
          <SpecValue>{fmt(waterAddedL, 2)} L</SpecValue> of water makes a{" "}
          <SpecValue>{fmt(rev_dilutedConc_mgL, 0)} mg/L</SpecValue>{" "}
          (<SpecValue>{fmt(rev_chemical_pct, 2)} % w/v</SpecValue>) solution in the{" "}
          <SpecValue>{mixingTankL} L</SpecValue> tank. At{" "}
          <SpecValue>{pumpBar} bar</SpecValue> the pump curve reads{" "}
          <SpecValue>{dosingPumpLh} L/h</SpecValue>;{" "}
          {pumpStrokePct < 100 ? (
            <>at <SpecValue>{pumpStrokePct} %</SpecValue> stroke the pump delivers{" "}
              <SpecValue>{fmt(activeDosingLh, 2)} L/h</SpecValue>{" "}
            </>
          ) : (
            <>at 100 % stroke the pump delivers the full{" "}
              <SpecValue>{dosingPumpLh} L/h</SpecValue>{" "}
            </>
          )}
          into <SpecValue>{flowVal} {UNITS.flow.labels[flowUnit]}</SpecValue>{" "}
          of process water — that&apos;s a resulting dose of{" "}
          <SpecValue>{fmt(rev_resulting_mgL, 2)} mg/L</SpecValue>. Tank lasts{" "}
          <SpecValue>{fmt(rev_runtime_h, 1)} h</SpecValue> continuous ({fmt(rev_runtime_days, 1)} days at {hoursPerDay} h/day). Daily stock consumption ≈{" "}
          <SpecValue>{fmt(rev_daily_L, 2)} L</SpecValue>, active ingredient ≈{" "}
          <SpecValue>{fmt(rev_daily_active_kg, 3)} kg</SpecValue>.
        </SpecCard>
      )}
    </ToolShell>
  );
}
